import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  BankAccount,
  CreateBankAccountPayload,
} from "../repositories/bankRepository";
import { bankRepository } from "../repositories/bankRepository";
import {
  bankAccountOptionsQueryKey,
  bankAccountQueryKey,
} from "./useBankAccountQueries";
import { bankAccountsQueryKey } from "./useBankAccountsQuery";
import { financialOverviewQueryKey } from "./useFinancialOverviewQuery";
import { financialQueryKeys } from "./useFinancialQueries";

type Context = {
  previous?: BankAccount[] | null;
  tempId?: string;
};

/**
 * Mutation hook to create a bank account.
 *
 * - Performs an optimistic insert into the accounts list.
 * - Rolls back on error.
 * - Updates cache from server response on success.
 * - Invalidates relevant queries on settled.
 */
export function useCreateBankAccountMutation() {
  const qc = useQueryClient();

  return useMutation<BankAccount, Error, CreateBankAccountPayload, Context>({
    mutationKey: ["bank-account", "create"],
    mutationFn: (payload) => bankRepository.createBankAccount(payload),

    onMutate: async (payload) => {
      const listKey = bankAccountsQueryKey();
      await qc.cancelQueries({ queryKey: listKey });

      const previous = qc.getQueryData<BankAccount[]>(listKey) ?? null;

      const tempId = `temp-${Date.now()}`;
      const optimistic: BankAccount = {
        id: tempId,
        name: payload.name,
        account: payload.account ?? "",
        agency: payload.agency ?? "",
        bankAccountOption: {
          id: payload.bankAccountOptionId ?? "",
          name: "",
        },
        initialBalance: String(payload.initialBalance ?? 0),
        currentBalance: String(payload.currentBalance ?? 0),
        pixKey: payload.pixKey ?? null,
        pixKeyType: (payload.pixKeyType as BankAccount["pixKeyType"]) ?? null,
        accountType:
          (payload.accountType as BankAccount["accountType"]) ?? "BANK",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      qc.setQueryData<BankAccount[] | null>(listKey, (old = []) => [
        optimistic,
        ...(old ?? []),
      ]);

      return { previous, tempId };
    },

    onError: (_err, _vars, context) => {
      const listKey = bankAccountsQueryKey();
      if (context?.previous !== undefined) {
        qc.setQueryData(listKey, context.previous);
      } else {
        qc.invalidateQueries({ queryKey: listKey });
      }
    },

    onSuccess: (created, _vars, context) => {
      // ensure single account cache is set
      qc.setQueryData(bankAccountQueryKey(created.id), created);

      // replace optimistic item in list if present
      const listKey = bankAccountsQueryKey();
      if (context?.tempId) {
        qc.setQueryData<BankAccount[] | undefined>(listKey, (old) =>
          (old ?? []).map((item) =>
            item.id === context.tempId ? created : item,
          ),
        );
      } else {
        // append if no temp item found
        qc.setQueryData<BankAccount[] | undefined>(listKey, (old) =>
          old ? [created, ...old] : [created],
        );
      }
    },

    onSettled: (_data, _error, vars) => {
      const terreiroId = vars?.terreiroId;
      const mediumId = vars?.mediumId;

      const promises: Promise<unknown>[] = [
        qc.invalidateQueries({ queryKey: bankAccountsQueryKey() }),
        qc.invalidateQueries({ queryKey: bankAccountOptionsQueryKey() }),
      ];

      if (terreiroId) {
        promises.push(
          qc.invalidateQueries({
            queryKey: financialQueryKeys.bankAccounts(terreiroId),
          }),
          qc.invalidateQueries({
            queryKey: financialQueryKeys.monthlyFees(terreiroId),
          }),
          qc.invalidateQueries({
            queryKey: financialQueryKeys.currentMonthIncome(
              terreiroId,
              undefined,
            ),
          }),
          qc.invalidateQueries({
            queryKey: financialQueryKeys.currentMonthOutcome(
              terreiroId,
              undefined,
            ),
          }),
          qc.invalidateQueries({
            queryKey: financialOverviewQueryKey({ terreiroId }),
          }),
        );
      } else if (mediumId) {
        promises.push(
          qc.invalidateQueries({
            queryKey: financialQueryKeys.mediumBankAccounts(mediumId),
          }),
          qc.invalidateQueries({
            queryKey: financialQueryKeys.currentMonthIncome(
              undefined,
              mediumId,
            ),
          }),
          qc.invalidateQueries({
            queryKey: financialQueryKeys.currentMonthOutcome(
              undefined,
              mediumId,
            ),
          }),
          qc.invalidateQueries({
            queryKey: financialOverviewQueryKey({ mediumId }),
          }),
        );
      }

      return Promise.all(promises);
    },

    retry: 0,
  });
}
